// AnimatedAfrica — dark "OMNI" landing visual, ported from the Alpha
// Direct OMNI ERP login page so Nako Pula's hero matches the same look
// and feel. Renders a real Mercator-projected Africa (d3-geo + topojson
// loaded from unpkg at runtime), with a glowing orange halo, dot-pattern
// fill, pulsing cities, animated trade routes, and clickable country
// hit-paths.
//
// The component keeps the original public API used by Welcome.tsx:
//   onSelect(code: RegionCode)   — fires when BW / ZA / NG is clicked
//   selected?: RegionCode | null — drives the HQ-style marker placement
// Non-live countries are inert but get a hover tooltip with quick stats.

import { useEffect, useRef } from 'react';
import type { RegionCode } from '@nako/core';

interface Props {
  onSelect: (code: RegionCode) => void;
  selected?: RegionCode | null;
  className?: string;
}

const LIVE_COUNTRIES: Record<string, RegionCode> = {
  '072': 'BW',
  '710': 'ZA',
  '566': 'NG',
  '716': 'ZW',
};

let scriptsLoadStarted = false;

function loadOmniScripts(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  const w = window as unknown as { d3?: unknown; topojson?: unknown };
  if (w.d3 && w.topojson) return Promise.resolve();
  if (scriptsLoadStarted) {
    return new Promise((resolve) => {
      const tick = () => {
        if (w.d3 && w.topojson) resolve();
        else setTimeout(tick, 80);
      };
      tick();
    });
  }
  scriptsLoadStarted = true;
  const inject = (src: string) =>
    new Promise<void>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('failed to load ' + src));
      document.head.appendChild(s);
    });
  return inject('https://unpkg.com/d3@7.8.5/dist/d3.min.js').then(() =>
    inject('https://unpkg.com/topojson-client@3.1.0/dist/topojson-client.min.js'),
  );
}

const HQ_NAMES: Record<RegionCode, string> = {
  BW: 'GABORONE',
  ZA: 'JOHANNESBURG',
  NG: 'LAGOS',
  ZW: 'HARARE',
};

export function AnimatedAfrica({ onSelect, selected = null, className }: Props) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const hqName = selected ? HQ_NAMES[selected] : HQ_NAMES.BW;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stage = stageRef.current;
    if (!stage) return;
    let cancelled = false;

    (async () => {
      try {
        await loadOmniScripts();
      } catch {
        const m = stage.querySelector('#nakoLoadMsg');
        if (m) m.textContent = 'OFFLINE · GEO DATA UNAVAILABLE';
        return;
      }
      if (cancelled) return;
      // d3 + topojson are loaded from CDN at runtime so the local
      // package isn't installed — keep these as loose `any` shims.

      const w = window as unknown as { d3: any; topojson: any };

      const AFRICA = new Set([
        '012',
        '024',
        '072',
        '086',
        '108',
        '120',
        '132',
        '140',
        '148',
        '174',
        '178',
        '180',
        '204',
        '226',
        '231',
        '232',
        '262',
        '266',
        '270',
        '288',
        '324',
        '384',
        '404',
        '426',
        '430',
        '434',
        '450',
        '454',
        '466',
        '478',
        '480',
        '504',
        '508',
        '516',
        '562',
        '566',
        '624',
        '646',
        '678',
        '686',
        '690',
        '694',
        '706',
        '710',
        '716',
        '728',
        '729',
        '732',
        '748',
        '768',
        '788',
        '800',
        '818',
        '834',
        '854',
        '894',
      ]);

      const COUNTRY_DATA: Record<
        string,
        {
          name: string;
          cap: string;
          gdp: string;
          delta: string;
          pop: string;
          pen: string;
          live?: RegionCode;
        }
      > = {
        '012': {
          name: 'ALGERIA',
          cap: 'ALGIERS',
          gdp: '$187B',
          delta: '+3.1%',
          pop: '45M',
          pen: '0.7%',
        },
        '024': {
          name: 'ANGOLA',
          cap: 'LUANDA',
          gdp: '$94B',
          delta: '+3.4%',
          pop: '36M',
          pen: '1.0%',
        },
        '072': {
          name: 'BOTSWANA',
          cap: 'GABORONE',
          gdp: '$18.9B',
          delta: '+4.1%',
          pop: '2.6M',
          pen: '3.1%',
          live: 'BW',
        },
        '108': {
          name: 'BURUNDI',
          cap: 'GITEGA',
          gdp: '$3.4B',
          delta: '+3.0%',
          pop: '13M',
          pen: '0.5%',
        },
        '120': {
          name: 'CAMEROON',
          cap: 'YAOUNDÉ',
          gdp: '$50B',
          delta: '+4.0%',
          pop: '28M',
          pen: '1.0%',
        },
        '132': {
          name: 'CABO VERDE',
          cap: 'PRAIA',
          gdp: '$2.4B',
          delta: '+5.1%',
          pop: '600K',
          pen: '2.4%',
        },
        '140': {
          name: 'CENTRAL AFR. REP.',
          cap: 'BANGUI',
          gdp: '$2.7B',
          delta: '+1.0%',
          pop: '5M',
          pen: '0.3%',
        },
        '148': {
          name: 'CHAD',
          cap: "N'DJAMENA",
          gdp: '$12B',
          delta: '+2.5%',
          pop: '17M',
          pen: '0.4%',
        },
        '174': {
          name: 'COMOROS',
          cap: 'MORONI',
          gdp: '$1.4B',
          delta: '+3.0%',
          pop: '900K',
          pen: '0.6%',
        },
        '178': {
          name: 'CONGO (REP.)',
          cap: 'BRAZZAVILLE',
          gdp: '$15B',
          delta: '+3.5%',
          pop: '6M',
          pen: '1.4%',
        },
        '180': {
          name: 'DR CONGO',
          cap: 'KINSHASA',
          gdp: '$69B',
          delta: '+5.7%',
          pop: '102M',
          pen: '0.2%',
        },
        '204': {
          name: 'BENIN',
          cap: 'PORTO-NOVO',
          gdp: '$19B',
          delta: '+5.6%',
          pop: '13M',
          pen: '0.8%',
        },
        '226': {
          name: 'EQ. GUINEA',
          cap: 'MALABO',
          gdp: '$12B',
          delta: '-2.5%',
          pop: '1.7M',
          pen: '0.8%',
        },
        '231': {
          name: 'ETHIOPIA',
          cap: 'ADDIS ABABA',
          gdp: '$156B',
          delta: '+6.0%',
          pop: '126M',
          pen: '0.4%',
        },
        '232': {
          name: 'ERITREA',
          cap: 'ASMARA',
          gdp: '$2.6B',
          delta: '+2.5%',
          pop: '3.7M',
          pen: '0.5%',
        },
        '262': {
          name: 'DJIBOUTI',
          cap: 'DJIBOUTI',
          gdp: '$3.9B',
          delta: '+5.0%',
          pop: '1M',
          pen: '1.2%',
        },
        '266': {
          name: 'GABON',
          cap: 'LIBREVILLE',
          gdp: '$21B',
          delta: '+2.7%',
          pop: '2.4M',
          pen: '1.8%',
        },
        '270': {
          name: 'GAMBIA',
          cap: 'BANJUL',
          gdp: '$2.4B',
          delta: '+5.0%',
          pop: '2.5M',
          pen: '0.5%',
        },
        '288': {
          name: 'GHANA',
          cap: 'ACCRA',
          gdp: '$76B',
          delta: '+3.1%',
          pop: '33M',
          pen: '1.1%',
        },
        '324': {
          name: 'GUINEA',
          cap: 'CONAKRY',
          gdp: '$22B',
          delta: '+5.5%',
          pop: '14M',
          pen: '0.6%',
        },
        '384': {
          name: "CÔTE D'IVOIRE",
          cap: 'YAMOUSSOUKRO',
          gdp: '$79B',
          delta: '+6.5%',
          pop: '28M',
          pen: '1.4%',
        },
        '404': {
          name: 'KENYA',
          cap: 'NAIROBI',
          gdp: '$114B',
          delta: '+5.0%',
          pop: '56M',
          pen: '2.4%',
        },
        '426': {
          name: 'LESOTHO',
          cap: 'MASERU',
          gdp: '$2.4B',
          delta: '+1.6%',
          pop: '2.3M',
          pen: '4.0%',
        },
        '430': {
          name: 'LIBERIA',
          cap: 'MONROVIA',
          gdp: '$4.0B',
          delta: '+4.7%',
          pop: '5.4M',
          pen: '0.7%',
        },
        '434': {
          name: 'LIBYA',
          cap: 'TRIPOLI',
          gdp: '$45B',
          delta: '+10.2%',
          pop: '7M',
          pen: '0.5%',
        },
        '450': {
          name: 'MADAGASCAR',
          cap: 'ANTANANARIVO',
          gdp: '$15B',
          delta: '+4.3%',
          pop: '30M',
          pen: '1.2%',
        },
        '454': {
          name: 'MALAWI',
          cap: 'LILONGWE',
          gdp: '$13B',
          delta: '+3.2%',
          pop: '21M',
          pen: '1.2%',
        },
        '466': {
          name: 'MALI',
          cap: 'BAMAKO',
          gdp: '$20B',
          delta: '+4.3%',
          pop: '22M',
          pen: '0.6%',
        },
        '478': {
          name: 'MAURITANIA',
          cap: 'NOUAKCHOTT',
          gdp: '$10B',
          delta: '+5.1%',
          pop: '4.8M',
          pen: '1.0%',
        },
        '480': {
          name: 'MAURITIUS',
          cap: 'PORT LOUIS',
          gdp: '$14B',
          delta: '+4.8%',
          pop: '1.3M',
          pen: '6.0%',
        },
        '504': {
          name: 'MOROCCO',
          cap: 'RABAT',
          gdp: '$147B',
          delta: '+3.5%',
          pop: '38M',
          pen: '3.9%',
        },
        '508': {
          name: 'MOZAMBIQUE',
          cap: 'MAPUTO',
          gdp: '$20B',
          delta: '+5.0%',
          pop: '33M',
          pen: '0.8%',
        },
        '516': {
          name: 'NAMIBIA',
          cap: 'WINDHOEK',
          gdp: '$13B',
          delta: '+3.7%',
          pop: '2.6M',
          pen: '6.2%',
        },
        '562': {
          name: 'NIGER',
          cap: 'NIAMEY',
          gdp: '$17B',
          delta: '+7.2%',
          pop: '26M',
          pen: '0.4%',
        },
        '566': {
          name: 'NIGERIA',
          cap: 'ABUJA',
          gdp: '$477B',
          delta: '+2.9%',
          pop: '220M',
          pen: '0.4%',
          live: 'NG',
        },
        '624': {
          name: 'GUINEA-BISSAU',
          cap: 'BISSAU',
          gdp: '$2.0B',
          delta: '+4.5%',
          pop: '2M',
          pen: '0.4%',
        },
        '646': {
          name: 'RWANDA',
          cap: 'KIGALI',
          gdp: '$13B',
          delta: '+7.0%',
          pop: '14M',
          pen: '1.7%',
        },
        '678': {
          name: 'SÃO TOMÉ & PR.',
          cap: 'SÃO TOMÉ',
          gdp: '$0.6B',
          delta: '+1.8%',
          pop: '220K',
          pen: '0.6%',
        },
        '686': {
          name: 'SENEGAL',
          cap: 'DAKAR',
          gdp: '$30B',
          delta: '+5.3%',
          pop: '17M',
          pen: '1.5%',
        },
        '690': {
          name: 'SEYCHELLES',
          cap: 'VICTORIA',
          gdp: '$1.8B',
          delta: '+3.0%',
          pop: '100K',
          pen: '5.2%',
        },
        '694': {
          name: 'SIERRA LEONE',
          cap: 'FREETOWN',
          gdp: '$4.0B',
          delta: '+3.4%',
          pop: '8M',
          pen: '0.6%',
        },
        '706': {
          name: 'SOMALIA',
          cap: 'MOGADISHU',
          gdp: '$8B',
          delta: '+2.8%',
          pop: '17M',
          pen: '0.1%',
        },
        '710': {
          name: 'SOUTH AFRICA',
          cap: 'PRETORIA · CAPE TOWN',
          gdp: '$377B',
          delta: '+1.8%',
          pop: '60M',
          pen: '13.4%',
          live: 'ZA',
        },
        '716': {
          name: 'ZIMBABWE',
          cap: 'HARARE',
          gdp: '$35B',
          delta: '+3.5%',
          pop: '16M',
          pen: '4.5%',
          live: 'ZW',
        },
        '728': {
          name: 'SOUTH SUDAN',
          cap: 'JUBA',
          gdp: '$4.0B',
          delta: '-0.6%',
          pop: '11M',
          pen: '0.2%',
        },
        '729': {
          name: 'SUDAN',
          cap: 'KHARTOUM',
          gdp: '$35B',
          delta: '-0.8%',
          pop: '48M',
          pen: '0.5%',
        },
        '732': {
          name: 'WESTERN SAHARA',
          cap: 'EL AAIÚN',
          gdp: '$0.9B',
          delta: '—',
          pop: '600K',
          pen: '—',
        },
        '748': {
          name: 'ESWATINI',
          cap: 'MBABANE',
          gdp: '$4.9B',
          delta: '+2.5%',
          pop: '1.2M',
          pen: '2.8%',
        },
        '768': { name: 'TOGO', cap: 'LOMÉ', gdp: '$9.0B', delta: '+5.4%', pop: '9M', pen: '1.6%' },
        '788': {
          name: 'TUNISIA',
          cap: 'TUNIS',
          gdp: '$48B',
          delta: '+2.5%',
          pop: '12M',
          pen: '2.0%',
        },
        '800': {
          name: 'UGANDA',
          cap: 'KAMPALA',
          gdp: '$52B',
          delta: '+5.6%',
          pop: '49M',
          pen: '0.9%',
        },
        '818': {
          name: 'EGYPT',
          cap: 'CAIRO',
          gdp: '$476B',
          delta: '+4.2%',
          pop: '110M',
          pen: '0.6%',
        },
        '834': {
          name: 'TANZANIA',
          cap: 'DODOMA',
          gdp: '$84B',
          delta: '+5.2%',
          pop: '67M',
          pen: '0.5%',
        },
        '854': {
          name: 'BURKINA FASO',
          cap: 'OUAGADOUGOU',
          gdp: '$22B',
          delta: '+5.0%',
          pop: '23M',
          pen: '1.1%',
        },
        '894': {
          name: 'ZAMBIA',
          cap: 'LUSAKA',
          gdp: '$29B',
          delta: '+3.8%',
          pop: '20M',
          pen: '1.4%',
        },
      };

      const svg = stage.querySelector<SVGSVGElement>('#nakoAfricaSvg');
      if (!svg) return;
      const W = 720;
      const H = 720;
      const PAD = 22;

      const setHTML = (id: string, html: string) => {
        const el = stage.querySelector('#' + id);
        if (el) el.innerHTML = html;
      };

      const urls = [
        'https://unpkg.com/world-atlas@2.0.2/countries-50m.json',
        'https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-50m.json',
        'https://unpkg.com/world-atlas@2/countries-110m.json',
      ];
      let world: { objects: { countries: { geometries: Array<{ id: string | number }> } } } | null =
        null;
      for (const u of urls) {
        try {
          const r = await fetch(u);
          if (r.ok) {
            world = await r.json();
            break;
          }
        } catch {
          /* try next */
        }
      }
      if (!world) {
        const m = stage.querySelector('#nakoLoadMsg');
        if (m) m.textContent = 'OFFLINE · GEO DATA UNAVAILABLE';
        return;
      }
      if (cancelled) return;
      const msg = stage.querySelector('#nakoLoadMsg');
      if (msg) msg.remove();

      const africaGeoms = world.objects.countries.geometries.filter((g) =>
        AFRICA.has(String(g.id).padStart(3, '0')),
      );

      const africaObj: any = { type: 'GeometryCollection', geometries: africaGeoms };

      const africaTopo: any = { ...world, objects: { ...world.objects, africa: africaObj } };

      const continent: any = w.topojson.merge(africaTopo, africaGeoms as any);

      const interior: any = w.topojson.mesh(africaTopo, africaObj, (a: any, b: any) => a !== b);

      const projection = w.d3.geoMercator().fitExtent(
        [
          [PAD, PAD],
          [W - PAD, H - PAD],
        ],
        continent,
      );
      const path = w.d3.geoPath(projection);
      const continentD = path(continent) ?? '';
      const interiorD = path(interior) ?? '';

      setHTML(
        'nakoLayerHalo',
        '<path d="' +
          continentD +
          '" fill="none" stroke="#F07F00" stroke-width="2"  opacity="0.55" filter="url(#nakoBigGlow)"/>' +
          '<path d="' +
          continentD +
          '" fill="none" stroke="#F07F00" stroke-width="6"  opacity="0.20" filter="url(#nakoBigGlow)"/>' +
          '<path d="' +
          continentD +
          '" fill="none" stroke="#F07F00" stroke-width="14" opacity="0.10" filter="url(#nakoBigGlow)"/>',
      );

      const defs = svg.querySelector('defs');
      const clipId = 'nakoClipContinent';
      defs?.insertAdjacentHTML(
        'beforeend',
        '<clipPath id="' + clipId + '"><path d="' + continentD + '"/></clipPath>',
      );
      setHTML(
        'nakoLayerCountriesFill',
        '<g clip-path="url(#' +
          clipId +
          ')">' +
          '<path d="' +
          continentD +
          '" fill="#F07F00" opacity="0.035"/>' +
          '<rect x="0" y="0" width="' +
          W +
          '" height="' +
          H +
          '" fill="url(#nakoDotsTight)"/>' +
          '<rect x="0" y="0" width="' +
          W +
          '" height="' +
          H +
          '" fill="url(#nakoDots)" opacity="0.55"/>' +
          '<rect x="0" y="0" width="' +
          W +
          '" height="80" fill="#F07F00" opacity="0.08">' +
          '<animate attributeName="y" from="-80" to="' +
          H +
          '" dur="14s" repeatCount="indefinite"/>' +
          '</rect>' +
          '</g>',
      );
      setHTML(
        'nakoLayerCountriesBorder',
        '<path d="' +
          interiorD +
          '" fill="none" stroke="#F07F00" stroke-width="0.5"  opacity="0.32" stroke-dasharray="1.5 2.2"/>' +
          '<path d="' +
          interiorD +
          '" fill="none" stroke="#FFB04A" stroke-width="0.25" opacity="0.5"/>',
      );
      setHTML(
        'nakoLayerContinent',
        '<path d="' +
          continentD +
          '" fill="none" stroke="#F07F00" stroke-width="1.6" filter="url(#nakoGlowSoft)"/>' +
          '<path d="' +
          continentD +
          '" fill="none" stroke="#FFD89A" stroke-width="0.7" opacity="0.95"/>',
      );

      const cities: Array<{ name: string; ll: [number, number] }> = [
        { name: 'CASABLANCA', ll: [-7.6, 33.6] },
        { name: 'CAIRO', ll: [31.2, 30.0] },
        { name: 'DAKAR', ll: [-17.4, 14.7] },
        { name: 'LAGOS', ll: [3.4, 6.5] },
        { name: 'ACCRA', ll: [-0.2, 5.6] },
        { name: 'ADDIS ABABA', ll: [38.7, 9.0] },
        { name: 'NAIROBI', ll: [36.8, -1.3] },
        { name: 'KINSHASA', ll: [15.3, -4.3] },
        { name: 'LUANDA', ll: [13.2, -8.8] },
        { name: 'JOHANNESBURG', ll: [28.0, -26.2] },
        { name: 'CAPE TOWN', ll: [18.4, -33.9] },
        { name: 'HARARE', ll: [31.05, -17.83] },
      ];
      // HQ marker tracks selected region; default to Botswana.
      const HQ_BY_REGION: Record<RegionCode, { name: string; ll: [number, number] }> = {
        BW: { name: 'GABORONE', ll: [25.92, -24.65] },
        ZA: { name: 'JOHANNESBURG', ll: [28.05, -26.2] },
        NG: { name: 'LAGOS', ll: [3.4, 6.5] },
        ZW: { name: 'HARARE', ll: [31.05, -17.83] },
      };
      const hqInfo = selected ? HQ_BY_REGION[selected] : HQ_BY_REGION.BW;

      let citiesHtml = '';
      let labelsHtml = '';
      cities.forEach((c, i) => {
        const [x, y] = projection(c.ll) as [number, number];
        const delay = ((i * 0.37) % 3).toFixed(2);
        citiesHtml +=
          '<g pointer-events="none">' +
          '<circle cx="' +
          x +
          '" cy="' +
          y +
          '" r="2.6" fill="#FFB04A"/>' +
          '<circle cx="' +
          x +
          '" cy="' +
          y +
          '" r="3" fill="none" stroke="#F07F00" stroke-width="1">' +
          '<animate attributeName="r"       from="3"   to="26" dur="3s" begin="' +
          delay +
          's" repeatCount="indefinite"/>' +
          '<animate attributeName="opacity" from="0.9" to="0"  dur="3s" begin="' +
          delay +
          's" repeatCount="indefinite"/>' +
          '</circle>' +
          '</g>';
        const lx = c.ll[0] < 5 ? x - 6 : x + 7;
        const ly = c.ll[1] > 5 ? y - 6 : y + 11;
        const ta = c.ll[0] < 5 ? 'end' : 'start';
        labelsHtml +=
          '<text x="' +
          lx.toFixed(1) +
          '" y="' +
          ly.toFixed(1) +
          '" text-anchor="' +
          ta +
          '">' +
          c.name +
          '</text>';
      });

      const [gx, gy] = projection(hqInfo.ll) as [number, number];
      citiesHtml +=
        '<g pointer-events="none">' +
        '<g filter="url(#nakoGlowHot)">' +
        '<circle cx="' +
        gx +
        '" cy="' +
        gy +
        '" r="9" fill="none" stroke="#FFB04A" stroke-width="1.2" opacity="0.85"/>' +
        '<circle cx="' +
        gx +
        '" cy="' +
        gy +
        '" r="5" fill="#FFFFFF"/>' +
        '<circle cx="' +
        gx +
        '" cy="' +
        gy +
        '" r="3" fill="none" stroke="#FFFFFF" stroke-width="1.2">' +
        '<animate attributeName="r"       from="6"   to="44" dur="2.6s" repeatCount="indefinite"/>' +
        '<animate attributeName="opacity" from="0.9" to="0"  dur="2.6s" repeatCount="indefinite"/>' +
        '</circle>' +
        '<circle cx="' +
        gx +
        '" cy="' +
        gy +
        '" r="3" fill="none" stroke="#F07F00" stroke-width="0.8">' +
        '<animate attributeName="r"       from="6"   to="84" dur="4s" repeatCount="indefinite"/>' +
        '<animate attributeName="opacity" from="0.7" to="0"  dur="4s" repeatCount="indefinite"/>' +
        '</circle>' +
        '</g>' +
        '<g stroke="#F07F00" stroke-width="0.7" opacity="0.7">' +
        '<line x1="' +
        (gx - 26) +
        '" y1="' +
        gy +
        '" x2="' +
        (gx - 9) +
        '" y2="' +
        gy +
        '"/>' +
        '<line x1="' +
        (gx + 9) +
        '" y1="' +
        gy +
        '" x2="' +
        (gx + 26) +
        '" y2="' +
        gy +
        '"/>' +
        '<line x1="' +
        gx +
        '" y1="' +
        (gy - 26) +
        '" x2="' +
        gx +
        '" y2="' +
        (gy - 9) +
        '"/>' +
        '<line x1="' +
        gx +
        '" y1="' +
        (gy + 9) +
        '" x2="' +
        gx +
        '" y2="' +
        (gy + 26) +
        '"/>' +
        '</g>' +
        '</g>';
      labelsHtml +=
        '<text x="' +
        (gx + 14) +
        '" y="' +
        (gy - 6) +
        '" fill="#FFFFFF" font-weight="700" font-size="11" letter-spacing="2.2">' +
        hqInfo.name +
        ' · NAKO</text>' +
        '<text x="' +
        (gx + 14) +
        '" y="' +
        (gy + 7) +
        '" fill="#F07F00" font-size="8" letter-spacing="1.6">PICK A REGION TO LAUNCH</text>';
      setHTML('nakoLayerCities', citiesHtml);
      setHTML('nakoLayerLabels', labelsHtml);

      let hitsHtml = '';
      africaGeoms.forEach((g) => {
        const iso = String(g.id).padStart(3, '0');
        if (!COUNTRY_DATA[iso]) return;

        const feat = w.topojson.feature(africaTopo, g as any);
        const d = path(feat);
        if (!d) return;
        const isLive = LIVE_COUNTRIES[iso] !== undefined;
        hitsHtml +=
          '<path class="nako-country-hit' +
          (isLive ? ' is-live' : '') +
          (selected && LIVE_COUNTRIES[iso] === selected ? ' is-selected' : '') +
          '" data-iso="' +
          iso +
          '" d="' +
          d +
          '"/>';
      });
      setHTML('nakoLayerCountryHits', hitsHtml);

      const tt = stage.querySelector<HTMLDivElement>('#nakoCityTooltip');
      const renderCountryTooltip = (iso: string) => {
        const d = COUNTRY_DATA[iso];
        if (!d) return '';
        const deltaCls = d.delta.startsWith('-') ? 'red' : d.delta === '—' ? 'dim' : 'green';
        const liveBadge = d.live ? ' · LIVE ON NAKO' : '';
        return (
          '<div class="name">' +
          d.name +
          '</div>' +
          '<div class="country">' +
          d.cap +
          liveBadge +
          '</div>' +
          '<hr/>' +
          '<div class="row"><span class="k">GDP</span><span class="v">' +
          d.gdp +
          ' <span class="' +
          deltaCls +
          '">' +
          d.delta +
          '</span></span></div>' +
          '<div class="row"><span class="k">POPULATION</span><span class="v">' +
          d.pop +
          '</span></div>' +
          '<hr/>' +
          '<div class="row"><span class="k">INS. PENETRATION</span><span class="v">' +
          d.pen +
          '</span></div>' +
          (d.live
            ? '<div class="row"><span class="k">ACTION</span><span class="v green">CLICK TO PICK</span></div>'
            : '<div class="row"><span class="k">STATUS</span><span class="v dim">COMING SOON</span></div>')
        );
      };
      const placeTooltip = (clientX: number, clientY: number) => {
        if (!tt) return;
        const r = stage.getBoundingClientRect();
        tt.style.left = clientX - r.left + 'px';
        tt.style.top = clientY - r.top + 'px';
      };
      const hideTooltip = () => tt?.classList.remove('visible');

      stage.querySelectorAll<SVGPathElement>('.nako-country-hit').forEach((p) => {
        p.addEventListener('mouseenter', (e) => {
          if (!tt) return;
          tt.innerHTML = renderCountryTooltip(p.dataset.iso || '');
          placeTooltip((e as MouseEvent).clientX, (e as MouseEvent).clientY);
          tt.classList.add('visible');
        });
        p.addEventListener('mousemove', (e) =>
          placeTooltip((e as MouseEvent).clientX, (e as MouseEvent).clientY),
        );
        p.addEventListener('mouseleave', hideTooltip);
        p.addEventListener('click', () => {
          const iso = p.dataset.iso || '';
          const code = LIVE_COUNTRIES[iso];
          if (code) onSelectRef.current(code);
        });
      });

      const links: Array<[string, string]> = [
        ['CASABLANCA', 'CAIRO'],
        ['CAIRO', 'ADDIS ABABA'],
        ['ADDIS ABABA', 'NAIROBI'],
        ['LAGOS', 'DAKAR'],
        ['DAKAR', 'CASABLANCA'],
        ['LAGOS', 'KINSHASA'],
        ['LUANDA', 'KINSHASA'],
        ['JOHANNESBURG', 'CAPE TOWN'],
        ['NAIROBI', 'ADDIS ABABA'],
      ];
      const lookup = Object.fromEntries(
        cities.concat([hqInfo]).map((c) => [c.name, projection(c.ll) as [number, number]]),
      );
      let connHtml = '';
      links.forEach((l, i) => {
        const a = lookup[l[0]];
        const b = lookup[l[1]];
        if (!a || !b) return;
        const dur = (2.2 + ((i * 0.18) % 2.5)).toFixed(2);
        connHtml +=
          '<line x1="' +
          a[0].toFixed(1) +
          '" y1="' +
          a[1].toFixed(1) +
          '" x2="' +
          b[0].toFixed(1) +
          '" y2="' +
          b[1].toFixed(1) +
          '">' +
          '<animate attributeName="stroke-dashoffset" from="0" to="-60" dur="' +
          dur +
          's" repeatCount="indefinite"/>' +
          '</line>';
      });
      setHTML(
        'nakoLayerConnections',
        '<g stroke="#F07F00" stroke-width="0.55" fill="none" opacity="0.42" stroke-dasharray="2 4">' +
          connHtml +
          '</g>',
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [selected]);

  return (
    <div
      ref={stageRef}
      className={'nako-stage ' + (className ?? '')}
      data-screen-label="NAKO Landing"
      role="region"
      aria-label="Africa map — pick a country to launch"
    >
      <div className="nako-grid" />
      <div className="nako-scan" />
      <div className="nako-ticks">
        <span className="tl" />
        <span className="tr" />
        <span className="bl" />
        <span className="br" />
      </div>

      <div className="nako-brand">
        <div className="product">
          <strong>NAKO</strong>&nbsp;PULA
        </div>
        <div className="divider" />
        <div className="eyebrow-mono">ERP · v2026</div>
      </div>

      <div className="nako-meta">
        <div className="row live">SYS // LIVE</div>
        <div className="row">4 REGIONS · {hqName}</div>
        <div className="row">v2026.05 · BUILD 1147</div>
      </div>

      <div className="nako-africa-wrap">
        <svg
          className="nako-africa"
          id="nakoAfricaSvg"
          viewBox="0 0 720 720"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <filter id="nakoBigGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="b1" />
              <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="b2" />
              <feGaussianBlur in="SourceGraphic" stdDeviation="22" result="b3" />
              <feMerge>
                <feMergeNode in="b3" />
                <feMergeNode in="b2" />
                <feMergeNode in="b1" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="nakoGlowSoft" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
            </filter>
            <filter id="nakoGlowHot" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="b1" />
              <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="b2" />
              <feGaussianBlur in="SourceGraphic" stdDeviation="20" result="b3" />
              <feMerge>
                <feMergeNode in="b3" />
                <feMergeNode in="b2" />
                <feMergeNode in="b1" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <pattern
              id="nakoDotsTight"
              x="0"
              y="0"
              width="9"
              height="9"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="4.5" cy="4.5" r="0.5" fill="#F07F00" opacity="0.45" />
            </pattern>
            <pattern id="nakoDots" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
              <circle cx="7" cy="7" r="0.9" fill="#F07F00" opacity="0.6" />
            </pattern>
          </defs>

          <g id="nakoLayerHalo" />
          <g id="nakoLayerCountriesFill" />
          <g id="nakoLayerCountriesBorder" />
          <g id="nakoLayerContinent" />
          <g id="nakoLayerConnections" />
          <g id="nakoLayerCities" />
          <g
            id="nakoLayerLabels"
            fontFamily="JetBrains Mono, ui-monospace, monospace"
            fontSize="9"
            letterSpacing="1.5"
            fill="#F8FAFC"
            stroke="rgba(13,27,42,0.55)"
            strokeWidth="0.4"
            paintOrder="stroke"
          />
          <g id="nakoLayerCountryHits" />
          <text
            id="nakoLoadMsg"
            x="360"
            y="360"
            textAnchor="middle"
            fill="#94A3B8"
            fontFamily="JetBrains Mono, ui-monospace, monospace"
            fontSize="11"
            letterSpacing="3"
            opacity="0.5"
          >
            LOADING GEOGRAPHIC DATA · NATURAL EARTH 1:50M
          </text>
        </svg>
      </div>

      <div className="nako-hero" aria-hidden="true">
        <div className="tagline">
          Built for Africa <em>by Africans.</em>
        </div>
        <div className="hint">
          <span className="pip" /> CLICK A GLOWING COUNTRY OR A BUTTON BELOW
        </div>
      </div>

      <div className="nako-cta-row">
        {(['BW', 'ZA', 'NG', 'ZW'] as RegionCode[]).map((code) => {
          const labels: Record<RegionCode, { flag: string; name: string; ccy: string }> = {
            BW: { flag: '🇧🇼', name: 'Botswana', ccy: 'P · Pula' },
            ZA: { flag: '🇿🇦', name: 'South Africa', ccy: 'R · Rand' },
            NG: { flag: '🇳🇬', name: 'Nigeria', ccy: '₦ · Naira' },
            ZW: { flag: '🇿🇼', name: 'Zimbabwe', ccy: 'US$ · Dollar' },
          };
          const l = labels[code];
          const isSelected = selected === code;
          return (
            <button
              key={code}
              type="button"
              className={'nako-region-chip' + (isSelected ? ' is-selected' : '')}
              onClick={() => onSelect(code)}
              aria-pressed={isSelected}
            >
              <span className="flag" aria-hidden="true">
                {l.flag}
              </span>
              <span className="text">
                <span className="name">{l.name}</span>
                <span className="ccy">{l.ccy}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="nako-city-tooltip" id="nakoCityTooltip" role="tooltip" />

      <style>{`
        .nako-stage {
          position: relative;
          width: 100%;
          aspect-ratio: 1 / 1;
          max-height: min(85vh, 820px);
          margin: 0 auto;
          border-radius: 24px;
          overflow: hidden;
          color: #ffffff;
          background:
            radial-gradient(ellipse 90% 70% at 50% 50%, rgba(240,127,0,0.10), transparent 70%),
            linear-gradient(175deg, #000000 0%, #0a0a0a 50%, #0d0d10 100%);
          box-shadow:
            0 0 0 1px rgba(240,127,0,0.12),
            0 40px 100px -20px rgba(0,0,0,0.5),
            inset 0 1px 0 rgba(255,255,255,0.03);
          font-family: 'Space Grotesk', system-ui, -apple-system, sans-serif;
          isolation: isolate;
          transition: box-shadow 0.3s ease;
        }
        .nako-stage:hover {
          box-shadow:
            0 0 0 1px rgba(240,127,0,0.25),
            0 50px 120px -20px rgba(0,0,0,0.6),
            inset 0 1px 0 rgba(255,255,255,0.05);
        }
        .nako-stage * { box-sizing: border-box; }
        .nako-grid {
          position: absolute; inset: -2px; pointer-events: none;
          background-image:
            linear-gradient(rgba(148,163,184,0.07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(148,163,184,0.07) 1px, transparent 1px);
          background-size: 60px 60px;
          mask-image: radial-gradient(ellipse 70% 70% at 50% 50%, #000 30%, transparent 90%);
          -webkit-mask-image: radial-gradient(ellipse 70% 70% at 50% 50%, #000 30%, transparent 90%);
          opacity: 0.55;
        }
        .nako-scan {
          position: absolute; inset: 0; pointer-events: none;
          background: repeating-linear-gradient(
            to bottom,
            transparent 0 3px,
            rgba(255,255,255,0.012) 3px 4px
          );
          mix-blend-mode: overlay;
        }
        .nako-ticks {
          position: absolute; inset: 16px; pointer-events: none;
          color: rgba(148,163,184,0.35);
        }
        .nako-ticks span {
          position: absolute; width: 12px; height: 12px; border: 1px solid currentColor;
        }
        .nako-ticks .tl { top: 0; left: 0; border-right: none; border-bottom: none; }
        .nako-ticks .tr { top: 0; right: 0; border-left: none; border-bottom: none; }
        .nako-ticks .bl { bottom: 0; left: 0; border-right: none; border-top: none; }
        .nako-ticks .br { bottom: 0; right: 0; border-left: none; border-top: none; }
        .nako-brand {
          position: absolute; top: 16px; left: 20px; z-index: 4;
          display: flex; align-items: center; gap: 14px;
        }
        .nako-brand .product {
          font-family: 'Book Antiqua', 'Palatino Linotype', Palatino, 'Hoefler Text', 'EB Garamond', Georgia, serif;
          font-size: 22px;
          font-style: italic;
          letter-spacing: 0.02em;
          color: #FFFFFF;
          text-transform: none;
        }
        .nako-brand .product strong {
          color: #F07F00;
          font-weight: 700;
          font-style: normal;
          font-size: 26px;
          letter-spacing: 0.02em;
        }
        .nako-brand .divider {
          width: 1px; height: 22px;
          background: rgba(255,255,255,0.18);
        }
        .nako-brand .eyebrow-mono {
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 10px;
          letter-spacing: 0.28em;
          color: rgba(148,163,184,0.7);
          text-transform: uppercase;
        }
        .nako-meta {
          position: absolute; top: 16px; right: 20px; z-index: 4;
          display: flex; flex-direction: column; align-items: flex-end; gap: 4px;
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 9.5px;
          letter-spacing: 0.28em;
          color: rgba(148,163,184,0.8);
          text-transform: uppercase;
        }
        .nako-meta .row { display: flex; align-items: center; gap: 8px; white-space: nowrap; }
        .nako-meta .live::before {
          content: ''; width: 6px; height: 6px; border-radius: 50%;
          background: #22c55e; box-shadow: 0 0 8px #22c55e; display: inline-block;
          animation: nako-blink 1.6s ease-in-out infinite;
        }
        @keyframes nako-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
        .nako-africa-wrap {
          position: absolute; inset: 0;
          display: grid; place-items: center;
          padding: 60px 12px 96px;
        }
        .nako-africa {
          width: min(86%, 720px);
          aspect-ratio: 1 / 1;
          filter: drop-shadow(0 0 60px rgba(240,127,0,0.18));
          animation: nako-breathe 9s ease-in-out infinite;
        }
        @keyframes nako-breathe {
          0%,100% { transform: scale(1); filter: drop-shadow(0 0 60px rgba(240,127,0,0.18)); }
          50%     { transform: scale(1.012); filter: drop-shadow(0 0 90px rgba(240,127,0,0.28)); }
        }
        .nako-hero {
          position: absolute; left: 0; right: 0; bottom: 78px;
          display: grid; place-items: center;
          padding: 0 24px;
          pointer-events: none;
          text-align: center;
          z-index: 4;
        }
        .nako-hero .tagline {
          font-family: 'Book Antiqua', 'Palatino Linotype', Palatino, 'URW Palladio L', 'Hoefler Text', 'EB Garamond', serif;
          font-weight: 400;
          font-style: italic;
          font-size: clamp(18px, 2.4vw, 30px);
          letter-spacing: 0.04em;
          color: rgba(255,255,255,0.92);
          text-shadow:
            0 0 12px rgba(13,27,42,0.85),
            0 0 28px rgba(13,27,42,0.65);
          opacity: 0;
          animation: nako-rise 1.1s 0.6s forwards cubic-bezier(.2,.7,.2,1);
        }
        .nako-hero .tagline em {
          color: #F07F00;
          font-style: normal;
          font-weight: 700;
        }
        .nako-hero .hint {
          margin-top: 10px;
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 10px;
          letter-spacing: 0.28em;
          color: rgba(255,176,74,0.85);
          text-transform: uppercase;
          display: inline-flex; align-items: center; gap: 8px;
          opacity: 0;
          animation: nako-rise 1.1s 0.85s forwards cubic-bezier(.2,.7,.2,1);
        }
        .nako-hero .hint .pip {
          width: 6px; height: 6px; border-radius: 50%;
          background: #F07F00;
          box-shadow: 0 0 10px #F07F00;
          animation: nako-blink 1.6s ease-in-out infinite;
        }
        @keyframes nako-rise {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .nako-cta-row {
          position: absolute; left: 0; right: 0; bottom: 14px;
          display: flex; justify-content: center; gap: 8px;
          padding: 0 10px;
          flex-wrap: wrap;
          z-index: 5;
        }
        .nako-region-chip {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 7px 12px;
          background: rgba(7,14,24,0.65);
          color: #fff;
          border: 1px solid rgba(240,127,0,0.42);
          border-radius: 999px;
          font-family: 'Space Grotesk', sans-serif;
          cursor: pointer;
          transition: background .15s ease, transform .15s ease, box-shadow .15s ease;
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }
        .nako-region-chip:hover {
          background: rgba(240,127,0,0.18);
          transform: translateY(-1px);
          box-shadow: 0 0 0 1px rgba(240,127,0,0.6), 0 0 22px rgba(240,127,0,0.35);
        }
        .nako-region-chip.is-selected {
          background: #F07F00;
          color: #1a0f00;
          border-color: rgba(255,255,255,0.4);
          box-shadow:
            0 0 0 1px rgba(240,127,0,0.6),
            0 0 24px rgba(240,127,0,0.55);
        }
        .nako-region-chip .flag { font-size: 18px; line-height: 1; }
        .nako-region-chip .text { display: flex; flex-direction: column; align-items: flex-start; line-height: 1.1; }
        .nako-region-chip .name { font-size: 11.5px; font-weight: 600; letter-spacing: 0.02em; }
        .nako-region-chip .ccy {
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 9px; letter-spacing: 0.18em;
          opacity: 0.7; text-transform: uppercase;
        }
        .nako-region-chip.is-selected .ccy { opacity: 0.85; color: #1a0f00; }

        .nako-city-tooltip {
          position: absolute; top: 0; left: 0;
          pointer-events: none;
          padding: 12px 14px 14px;
          background: rgba(7,14,24,0.94);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          border: 1px solid rgba(240,127,0,0.4);
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 10.5px;
          line-height: 1.4;
          letter-spacing: 0.05em;
          color: #fff;
          min-width: 240px;
          box-shadow:
            0 0 0 1px rgba(240,127,0,0.15),
            0 0 30px rgba(240,127,0,0.25),
            0 16px 50px rgba(0,0,0,0.7);
          z-index: 20;
          opacity: 0;
          transform: translate(-50%, calc(-100% - 16px)) translateY(6px);
          transition: opacity .15s ease, transform .15s ease;
          text-transform: uppercase;
          white-space: nowrap;
        }
        .nako-city-tooltip.visible {
          opacity: 1;
          transform: translate(-50%, calc(-100% - 16px));
        }
        .nako-city-tooltip .name { font-size: 12.5px; font-weight: 700; letter-spacing: 0.22em; color: #ffb04a; }
        .nako-city-tooltip .country { color: #94a3b8; font-size: 8.5px; letter-spacing: 0.32em; margin-top: 2px; }
        .nako-city-tooltip hr { border: none; border-top: 1px solid rgba(148,163,184,0.18); margin: 8px 0; }
        .nako-city-tooltip .row { display: flex; justify-content: space-between; gap: 18px; margin: 3px 0; font-size: 10px; letter-spacing: 0.16em; }
        .nako-city-tooltip .row .k { color: #94a3b8; }
        .nako-city-tooltip .row .v { color: #fff; font-weight: 500; }
        .nako-city-tooltip .row .v.green { color: #22c55e; }
        .nako-city-tooltip .row .v.red { color: #ef4444; }
        .nako-city-tooltip .row .v.dim { color: #64748b; }
        .nako-city-tooltip::after {
          content: ''; position: absolute; left: 50%; bottom: -7px;
          transform: translateX(-50%) rotate(45deg);
          width: 10px; height: 10px;
          background: rgba(7,14,24,0.94);
          border-right: 1px solid rgba(240,127,0,0.4);
          border-bottom: 1px solid rgba(240,127,0,0.4);
        }

        .nako-stage .nako-country-hit {
          fill: rgba(240,127,0,0.001);
          stroke: rgba(255,176,74,0);
          stroke-width: 1.4;
          cursor: crosshair;
          transition: fill .18s ease, stroke .18s ease;
        }
        .nako-stage .nako-country-hit:hover {
          fill: rgba(240,127,0,0.13);
          stroke: #ffd89a;
        }
        .nako-stage .nako-country-hit.is-live {
          fill: rgba(240,127,0,0.18);
          stroke: rgba(255,176,74,0.75);
          stroke-width: 1.2;
          cursor: pointer;
          animation: nako-live-pulse 3.2s ease-in-out infinite;
        }
        @keyframes nako-live-pulse {
          0%, 100% { fill: rgba(240,127,0,0.14); }
          50%      { fill: rgba(240,127,0,0.28); }
        }
        .nako-stage .nako-country-hit.is-live:hover {
          fill: rgba(255,255,255,0.18);
          stroke: #ffffff;
          animation: none;
        }
        .nako-stage .nako-country-hit.is-selected,
        .nako-stage .nako-country-hit.is-live.is-selected {
          fill: rgba(255,255,255,0.28);
          stroke: #ffffff;
          stroke-width: 1.6;
          animation: none;
        }
      `}</style>
    </div>
  );
}
